import type { Channel } from "./channel";
import type { Position } from "./model";
import { createSubscriber } from "./subscriber";

type EmscriptenFS = { writeFile: (path: string, data: string) => void };
type EmscriptenMod = {
  cwrap: (
    name: string,
    returnType: string,
    argTypes: string[],
  ) => (...args: number[]) => number;
  _malloc: (size: number) => number;
  _free: (ptr: number) => void;
};

export const createSimulationChannel = async ({
  position,
}: {
  position: Position;
}) => {
  const pollInterval = 20;

  const { subscribe: receive, emit } = createSubscriber<Uint8Array>();

  const resp = await fetch(new URL("./simulation.zip", import.meta.url));
  if (!resp.ok) throw new Error(`HTTP ${resp.status} fetching `);
  const { unzipSync } = await import("fflate");
  const files = unzipSync(new Uint8Array(await resp.arrayBuffer()));

  const decoder = new TextDecoder();

  const blob = new Blob([decoder.decode(files["arduplane.js"])], {
    type: "application/javascript",
  });
  const url = URL.createObjectURL(blob);

  if (!crossOriginIsolated)
    throw new Error(
      "The simulator requires cross-origin isolation for shared wasm memory.",
    );

  const wasmMemory = new WebAssembly.Memory({
    initial: 2 ** 8,
    maximum: 2 ** 15,
    shared: true,
  });

  const { default: factory } = (await import(/* @vite-ignore */ url)) as {
    default: (opts: unknown) => Promise<EmscriptenMod>;
  };

  const mod = await factory({
    wasmMemory,
    wasmBinary: files["arduplane.wasm"],
    mainScriptUrlOrBlob: url,
    locateFile: (_: string) => _,
    arguments: [
      "--model=sa_gd2000",
      "--defaults=sa_gd2000.parm,simulation.parm",
      "--serial1=none",
      "--serial2=none",
    ],
    preRun: [
      ({ FS }: { FS: EmscriptenFS }) => {
        const [lon, lat, alt] = position;
        FS.writeFile(
          "simulation.parm",
          `
SIM_OPOS_LAT=${lat}
SIM_OPOS_LNG=${lon}
SIM_OPOS_ALT=${alt}
SIM_SPEEDUP=10
`,
        );
        FS.writeFile("sa_gd2000.parm", decoder.decode(files["sa_gd2000.parm"]));
      },
    ],

    print: console.log,

    printErr: console.log,
  });

  const write: (ptr: number, len: number) => number = mod.cwrap(
    "ardupilot_serial0_write",
    "number",
    ["number", "number"],
  );
  const read: (ptr: number, len: number) => number = mod.cwrap(
    "ardupilot_serial0_read",
    "number",
    ["number", "number"],
  );
  const available: () => number = mod.cwrap(
    "ardupilot_serial0_read_available",
    "number",
    [],
  );

  const bufferSize = 4096;
  const buffer = mod._malloc(bufferSize);

  const timer = setInterval(() => {
    const n = available();
    if (n <= 0) return;
    const got = read(buffer, Math.min(n, bufferSize));
    if (got <= 0) return;
    emit(new Uint8Array(wasmMemory.buffer).slice(buffer, buffer + got));
  }, pollInterval);

  const send = (data: Uint8Array) => {
    new Uint8Array(wasmMemory.buffer).set(data, buffer);
    write(buffer, data.length);
    return Promise.resolve();
  };

  const close = () => {
    clearInterval(timer);
    mod._free(buffer);
  };

  return {
    send,
    receive,
    close,
  } satisfies Channel<Uint8Array>;
};
